/**
 * The Uploaded files list's rows and its columns' sorting and filtering, the
 * screen's logic without React, so node --test reads it directly. Every
 * column sorts; File Category and Status filter by the values the list holds
 * (a tick list, as the analytics GUI's file uploader does); the others search
 * as text, with the matches highlighted (columnSearch.jsx), and the upload
 * time by a from-to range, as the Error Log does.
 *
 * All of it runs over the batches already read (GET intake/batches, at most
 * one page): the Kind switch above the list is still the server's filter.
 */
import { colomboDateTime } from './errorRows.js';
import { CLASS_LABEL, acceptedBySystem, kindLabelOf } from './uploadKinds.js';

/**
 * Each batch with the words its columns show and search: the category and
 * kind in words, the rows as text, the upload time as `DD/MM/YYYY HH:mm`
 * with its `YYYYMMDDHHmm` key, and the uploader's name.
 */
export function toListRows(batches) {
  return (batches ?? []).map((b) => {
    const uploaded = colomboDateTime(b.createdAt);
    return {
      ...b,
      categoryLabel: CLASS_LABEL[b.fileClass] ?? b.fileClass ?? '—',
      kindLabel: kindLabelOf(b),
      rowsText: b.rowCount === null || b.rowCount === undefined ? '—' : String(b.rowCount),
      uploadedAt: uploaded.dateTime,
      uploadedSortKey: uploaded.sortKey,
      uploadedByLabel: b.uploadedByName ?? '—',
    };
  });
}

/** Load more: the next page of batches under those listed, a batch listed once (one uploaded meanwhile is already at the top). */
export function appendBatches(batches, page) {
  const seen = new Set(batches.map((b) => b.batchId));
  return [...batches, ...(page ?? []).filter((b) => !seen.has(b.batchId))];
}

/** A column's tick list: each value the rows hold, once, in the order of its words. */
export function filterOptions(rows, valueOf, textOf = valueOf) {
  const seen = new Map();
  for (const row of rows ?? []) {
    const value = valueOf(row);
    if (value !== null && value !== undefined && !seen.has(value)) seen.set(value, String(textOf(row)));
  }
  return [...seen].map(([value, text]) => ({ text, value })).sort((a, b) => a.text.localeCompare(b.text));
}

/** Sorts a column as text, ignoring case, with any numbers in it in number order. */
export const byText = (field) => (a, b) => String(a[field] ?? '').localeCompare(String(b[field] ?? ''), 'en', { numeric: true, sensitivity: 'base' });

/** Sorts a column as a number, an empty one first. */
export const byNumber = (field) => (a, b) => (a[field] ?? -1) - (b[field] ?? -1);

/** What follows each state, in a few words, for a row that offers no Approve or Reject. */
const NEXT_STEP = Object.freeze({
  RECEIVED: 'Checking the file…',
  SCANNING: 'Checking the file…',
  ACCEPTED: 'Being processed',
  PROCESSED: 'Complete',
  REJECTED: 'No further action',
  SUPERSEDED: 'Replaced by a newer file',
  QUARANTINED: 'Correct and upload again',
  SCAN_FAILED: 'Upload again',
  REJECTED_MALWARE: 'Not usable',
  ACCEPTANCE_EXPIRED: 'Upload again',
});

/**
 * The words in the actions cell of a row without Approve and Reject. A
 * VALIDATED file says who it waits for: none if the system accepts it, another
 * person if this one uploaded it, someone who also holds core.intake.initial
 * for an initial load this person may otherwise decide; any other state says
 * what follows it.
 */
export function rowNote(batch, { own = false, canAccept = false, canReject = false, canInitial = false } = {}) {
  if (batch?.state !== 'VALIDATED') return NEXT_STEP[batch?.state] ?? '';
  if (acceptedBySystem(batch)) return 'Accepted automatically';
  if (own) return 'Awaiting another approver';
  if (batch.loadKind === 'INITIAL' && (canAccept || canReject) && !canInitial) return 'Needs an initial-load approver';
  return 'Awaiting approval';
}
